import { Immutable } from "immer";
import React, { useEffect, useRef, useState } from "react";
import { t } from "ttag";

import { reverse } from "@reactivated";

import * as userAPI from "../../api/user";
import { Form } from "../../forms/Form";
import { FormButtonLink } from "../../forms/FormButtonLink";
import { FormHelpLink } from "../../forms/FormHelpLink";
import { FormInputWithoutFormatting as FormInput } from "../../forms/FormInputWithoutFormatting";
import { FormSubmit } from "../../forms/FormSubmit";
import { NonFieldErrors } from "../../forms/NonFieldErrors";
import { WebPageURL, isoWebPageURL } from "../../models/nominals";
import { updateGTMUserHash } from "../../utils/analytics";
import { navigateToURL } from "../../utils/urls";

type IProps = Immutable<{
    nextURL?: WebPageURL;
    initialEmail?: string;
}> & {
    onAfterLoad?: () => void;
    onAfterSave?: () => void;
};

export const LoginForm = (props: IProps): React.ReactNode => {
    const initialEmailValue = props.initialEmail ?? "";
    const [isSubmitting, setIsSubmitting] = useState(false);
    const emailRef = useRef(initialEmailValue);
    const passwordRef = useRef("");
    const [errors, setErrors] = useState({
        non_field_errors: [] as string[],
        email: [] as string[],
        password: [] as string[],
    });
    const [helptext, setHelptext] = useState<{
        email?: string;
    }>({});

    useEffect(() => {
        if (props.onAfterLoad) {
            props.onAfterLoad();
        }
        // intentional: fire once on mount only
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onEmailChange = (event: React.FormEvent<HTMLInputElement>) => {
        emailRef.current = event.currentTarget.value;
    };

    const onPasswordChange = (event: React.FormEvent<HTMLInputElement>) => {
        passwordRef.current = event.currentTarget.value;
    };

    const onSubmit = async (
        event: React.FormEvent<HTMLFormElement>,
    ): Promise<void> => {
        event.preventDefault();
        setIsSubmitting(true);
        try {
            const data = await userAPI.login(
                emailRef.current,
                passwordRef.current,
                props.nextURL,
            );

            if (data.redirect) {
                navigateToURL(data.redirect);
                return;
            }
            setIsSubmitting(false);
            setErrors({
                non_field_errors: [],
                email: [],
                password: [],
            });
            setHelptext({
                email: data.message,
            });
        } catch (error) {
            const data =
                error instanceof Error && "response" in error
                    ? (
                          error as {
                              response: { body: Record<string, string[]> };
                          }
                      ).response.body
                    : {};
            setIsSubmitting(false);
            setErrors({
                non_field_errors: data.non_field_errors || [],
                email: data.email || [],
                password: data.password || [],
            });
            setHelptext({
                email: "",
            });
        }
        updateGTMUserHash(emailRef.current);
        if (props.onAfterSave) {
            props.onAfterSave();
        }
    };

    return (
        <Form className="js-kuid-form" onSubmit={onSubmit}>
            <NonFieldErrors
                id="login_non_field"
                errors={errors.non_field_errors}
            />
            <FormInput
                id="account_email"
                name="account_email"
                type="email"
                errors={errors.email}
                placeholder={t`Email Address`}
                defaultValue={initialEmailValue}
                helpText={helptext.email}
                onChange={onEmailChange}
                required={true}
                disabled={isSubmitting}
                autoComplete="email"
            />
            <FormInput
                id="account_password"
                name="account_password"
                type="password"
                errors={errors.password}
                placeholder={t`Password`}
                onChange={onPasswordChange}
                disabled={isSubmitting}
                autoComplete="current-password"
            />
            <FormHelpLink
                href={isoWebPageURL.wrap(reverse("password-reset"))}
                helpText={t`Forgot password?`}
            />
            <FormSubmit
                value={t`Log in`}
                className="button button--full-width"
                disabled={isSubmitting}
            />
            <FormButtonLink
                href={isoWebPageURL.wrap(reverse("customer:register"))}
                text={t`Or, create account`}
            />
        </Form>
    );
};
